import React from 'react';
import { ChevronRight, DollarSign, BarChart2, Droplet, Leaf } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент сравнения различных видов топлива
 */
const FuelComparisonChart = ({ comparisonResults, activeTab, setActiveTab }) => {
  if (!comparisonResults || comparisonResults.length === 0) return null;

  // Сортировка результатов по стоимости от меньшей к большей
  const sortedByCost = [...comparisonResults].sort((a, b) => a.fuelCost - b.fuelCost);
  
  // Сортировка результатов по эко-показателям (выбросы CO2) от меньших к большим
  const sortedByEco = [...comparisonResults].sort((a, b) => a.co2Emissions - b.co2Emissions);
  
  // Сортировка результатов по расходу топлива от меньшего к большему
  const sortedByConsumption = [...comparisonResults].sort((a, b) => a.fuelConsumption - b.fuelConsumption);

  // Определение текущего набора данных в зависимости от вкладки
  const currentData = 
    activeTab === 'eco' ? sortedByEco : 
    activeTab === 'consumption' ? sortedByConsumption : 
    sortedByCost;
  
  // Нахождение максимальной стоимости для масштабирования графика
  const maxCost = Math.max(...currentData.map(item => 
    activeTab === 'cost' ? item.fuelCost : 
    activeTab === 'consumption' ? item.fuelConsumption : 
    item.co2Emissions
  ));

  // Текущий тип топлива
  const currentFuelType = comparisonResults.find(item => item.isCurrent)?.fuelType;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-blue-500" />
          Сравнение видов топлива
        </h3>
        
        {/* Вкладки для разных типов сравнения */}
        <div className="inline-flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            className={`px-3 py-1.5 text-sm ${
              activeTab === 'cost'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
            onClick={() => setActiveTab('cost')}
          >
            <span className="flex items-center">
              <DollarSign className="w-4 h-4 mr-1" />
              Стоимость
            </span>
          </button>
          <button
            className={`px-3 py-1.5 text-sm ${
              activeTab === 'consumption'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
            onClick={() => setActiveTab('consumption')}
          >
            <span className="flex items-center">
              <Droplet className="w-4 h-4 mr-1" />
              Расход
            </span>
          </button>
          <button
            className={`px-3 py-1.5 text-sm ${
              activeTab === 'eco'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
            onClick={() => setActiveTab('eco')}
          >
            <span className="flex items-center">
              <Leaf className="w-4 h-4 mr-1" />
              Экология
            </span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[400px] chart-container">
        <div className="min-w-[600px]">
          {currentData.map((item, index) => {
            // Расчет ширины полосы в зависимости от типа данных
            let value, maxValue, unit, percentage, color;
            
            if (activeTab === 'cost') {
              value = item.fuelCost;
              maxValue = maxCost;
              unit = '₽';
              percentage = (value / maxValue) * 100;
              color = item.isCurrent 
                ? 'bg-blue-500 dark:bg-blue-400' 
                : value < comparisonResults.find(i => i.isCurrent)?.fuelCost
                  ? 'bg-green-500 dark:bg-green-400'
                  : 'bg-red-500 dark:bg-red-400';
            } else if (activeTab === 'consumption') {
              value = item.fuelConsumption;
              maxValue = Math.max(...comparisonResults.map(i => i.fuelConsumption));
              unit = item.fuelType.id === 'electricity' ? 'кВт·ч/100км' : 'л/100км';
              percentage = (value / maxValue) * 100;
              color = item.isCurrent 
                ? 'bg-blue-500 dark:bg-blue-400' 
                : value < comparisonResults.find(i => i.isCurrent)?.fuelConsumption
                  ? 'bg-green-500 dark:bg-green-400'
                  : 'bg-red-500 dark:bg-red-400';
            } else { // eco tab
              value = item.co2Emissions;
              maxValue = Math.max(...comparisonResults.map(i => i.co2Emissions));
              unit = 'кг CO₂';
              percentage = (value / maxValue) * 100;
              color = item.isCurrent 
                ? 'bg-blue-500 dark:bg-blue-400' 
                : value < comparisonResults.find(i => i.isCurrent)?.co2Emissions
                  ? 'bg-green-500 dark:bg-green-400'
                  : 'bg-red-500 dark:bg-red-400';
            }

            return (
              <div key={item.fuelType.id} className="mb-3">
                <div className="flex items-center mb-1">
                  <div className="w-32 flex-shrink-0 text-sm text-gray-700 dark:text-gray-300">
                    {item.isCurrent && (
                      <ChevronRight className="w-4 h-4 inline text-blue-500 mr-1" />
                    )}
                    {item.fuelType.name}
                  </div>
                  <div className="flex-grow h-7 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden relative">
                    <div 
                      className={`h-full ${color} transition-all duration-500 ease-out`} 
                      style={{ width: `${percentage}%` }}
                    ></div>
                    <div className="absolute top-0 left-2 h-full flex items-center text-sm font-medium text-white">
                      {activeTab === 'cost' 
                        ? formatCurrency(value) 
                        : `${formatNumber(value, 1)} ${unit}`}
                    </div>
                  </div>
                </div>
                
                {/* Отображение процентной разницы с текущим выбранным топливом */}
                {!item.isCurrent && currentFuelType && activeTab === 'cost' && (
                  <div className="pl-32 text-xs">
                    {item.costDifference > 0 ? (
                      <span className="text-green-600 dark:text-green-400">
                        Экономия: {formatCurrency(item.costDifference)} ({formatNumber(Math.abs(item.costDifferencePercent), 0)}%)
                      </span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">
                        Дороже на: {formatCurrency(Math.abs(item.costDifference))} ({formatNumber(Math.abs(item.costDifferencePercent), 0)}%)
                      </span>
                    )}
                  </div>
                )}
                
                {!item.isCurrent && currentFuelType && activeTab === 'eco' && (
                  <div className="pl-32 text-xs">
                    {item.co2Difference > 0 ? (
                      <span className="text-green-600 dark:text-green-400">
                        Меньше выбросов: {formatNumber(Math.abs(item.co2Difference), 1)} кг ({formatNumber(Math.abs(item.co2DifferencePercent), 0)}%)
                      </span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">
                        Больше выбросов: {formatNumber(Math.abs(item.co2Difference), 1)} кг ({formatNumber(Math.abs(item.co2DifferencePercent), 0)}%)
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="bg-gray-50 dark:bg-gray-800/70 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
        <p className="flex items-start">
          <BarChart2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-blue-500" />
          <span>
            {activeTab === 'cost' && (
              "Сравнение стоимости поездки на разных видах топлива. Цены соответствуют средним по России. Экономия или дополнительные расходы рассчитаны относительно выбранного вами типа топлива."
            )}
            {activeTab === 'consumption' && (
              "Сравнение расхода топлива в эквивалентных единицах. Для электромобилей расход представлен в кВт·ч/100км, для газовых в м³/100км, для остальных в л/100км."
            )}
            {activeTab === 'eco' && (
              "Сравнение экологического воздействия разных видов топлива через выбросы CO₂. Электромобили имеют наименьшие прямые выбросы, газ занимает промежуточное положение, бензин и дизель имеют наибольший углеродный след."
            )}
          </span>
        </p>
      </div>
    </div>
  );
};

export default FuelComparisonChart;